// USER MANUAL — single source of the in-app help text.
//
// Everything here is written from the app's actual behaviour. If behaviour
// changes, edit this file (and bump MANUAL_UPDATED) — the /help page renders
// it verbatim, so there is exactly one place to keep true.
//
// Block types the renderer understands:
//   { p: "paragraph" }                     · *stars* mark bold
//   { list: ["item", ...] }                · bullets
//   { steps: ["do this", ...] }            · numbered
//   { defs: [["term", "meaning"], ...] }   · definition rows
//   { table: { head: [...], rows: [[...]] } }
//   { note: "..." }  { warn: "..." }       · callouts
//
// `roles` on a topic limits who sees it by default (null = everyone).

export const MANUAL_UPDATED = "7 September 2026";

export const ROLE_LABEL = {
  admin: "Admin",
  principal: "Principal",
  associate: "Associate",
  leave_editor: "Leave editor",
};

export const MANUAL = [
  // ─────────────────────────────────────────────────────────── 1 · START
  {
    id: "start",
    title: "Start here",
    blurb: "What this app is, how to get in, and what your login can see.",
    topics: [
      {
        id: "what-this-is",
        title: "What this app is",
        blocks: [
          { p: "One shared record of the firm's *fee forecast*, *invoicing* and *resourcing plan*. There is a single dataset — when you save, everyone sees it. Nothing is private to your screen." },
          { p: "Three questions it exists to answer: what revenue is planned month by month, what has actually been invoiced against that plan, and who is booked to deliver it." },
          {
            defs: [
              ["Report date", "The dataset's own date stamp, shown top-right. It moves when a fresh spreadsheet export is re-fed."],
              ["As-at month", "The current calendar month in New Zealand. Grids mark it with a small dot. Months before it are history; months after it are plan."],
              ["Stored currency", "Every figure is stored in NZD. A project can display a second currency alongside, but NZD is always the truth."],
            ],
          },
        ],
      },
      {
        id: "signing-in",
        title: "Signing in, passwords and lockouts",
        blocks: [
          { p: "Sign in with your firm email address and the password an admin issued you." },
          { p: "Passwords are *generated by an admin* on the Users page (or via login access on Personnel) and shown *once*, in a dialog with a copy button. If it was not copied at that moment it cannot be read back — an admin issues a new one." },
          { warn: "After 5 failed attempts on the same email or from the same network within 15 minutes, further attempts are refused with \"Too many failed login attempts. Wait 15 minutes and try again.\" Waiting it out clears it; so does a successful login." },
          { p: "To sign out: the door icon top-right on the portfolio, or *More actions → Sign out* on the admin header." },
        ],
      },
      {
        id: "roles",
        title: "What your login can see",
        blocks: [
          { p: "There are four kinds of login. The pages in your top navigation are the pages you have." },
          {
            defs: [
              ["Admin", "Everything, firm-wide. Overview, Sheet, Capacity (including Approvals), Principals, Allocation, Leave, Personnel, Users and My Projects. Can edit any project, approve resourcing, manage people and logins, re-feed the dataset and restore backups."],
              ["Principal", "My Portfolio, Add Project, My Summary, Capacity and Leave. You edit your own projects. The capacity matrix is firm-wide so you can see who is free — but it carries no money figures for anyone else's work."],
              ["Associate", "Portfolio, Summary and New Project — limited to the projects you have been added to as an editor, plus any you create."],
              ["Leave editor", "Projects (firm-wide, editable) and Leave (editable). Built for the person who keeps the leave calendar and helps maintain project records."],
            ],
          },
          { note: "Only admins see the Approvals tab, Personnel, Users, Re-feed and Backups. If you cannot find one of those, that is why." },
        ],
      },
      {
        id: "toolbar",
        title: "The toolbar on every admin page",
        roles: ["admin"],
        blocks: [
          { p: "The header carries controls that apply to *every* view at once, so a number never changes basis without you knowing." },
          {
            defs: [
              ["Report date", "Hover the info dot to see when the dataset was imported and by whom."],
              ["View as", "Scope the whole app to one principal's portfolio — useful for checking what they see, or editing on their behalf. An amber banner stays on screen while it is active; leave via View as → Firm-wide."],
              ["Re-feed", "Replace the dataset with a fresh spreadsheet export. A backup is taken first."],
              ["More actions (⋮)", "Backups, Refresh from dataset, Sign out."],
              ["Scenario", "Confirmed or Forecast. Affects money only — never resourcing."],
              ["Period", "Narrow every view to a run of months, e.g. one quarter. The counter on the right reads \"Showing 8 of 12 months\"."],
            ],
          },
        ],
      },
      {
        id: "reading-grids",
        title: "How to read any grid",
        blocks: [
          { p: "The fee grid, invoicing grid, team grid, capacity matrix and leave calendar all follow the same conventions." },
          {
            list: [
              "A *small dot* on a month header marks the as-at month.",
              "*Past months are shaded* and read-only. Editing them needs a per-account override that firm management grants to a named few — it is not given by role.",
              "*Amber with a padlock* on the invoicing grid means the month is later than the as-at month, so it cannot be invoiced.",
              "*Green with a tick* on an invoicing month means it has been confirmed and is read-only until reverted to draft.",
              "Grids scroll sideways — click and drag inside them, or use the scrollbar.",
            ],
          },
          { note: "Money cells accept a *ratio* as well as a figure. Typing \"5/10\" or \"0.5\" in a month means half of that row's contract value; typing 35000 means $35,000." },
          { warn: "A project form saves *only* when you press Save. There is no autosave — deliberately. Leaving with unsaved edits raises a browser warning. The leave calendar and the approvals queue are the exceptions: they save the moment you click." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────── 2 · VOCABULARY
  {
    id: "words",
    title: "The words we use",
    blurb: "Every term that appears on a tile, header or tooltip — defined once.",
    topics: [
      {
        id: "contract-status",
        title: "Contract status — Yes, Tentative, No",
        blocks: [
          { p: "Set per scope row on a project's fee grid. It decides whether that row's money counts, and where." },
          {
            defs: [
              ["Yes (Confirmed)", "Signed contract. Counts as Backlog, and in both scenarios."],
              ["Tentative", "Likely but not yet signed. Counts as Pipeline, and only in the Forecast scenario."],
              ["No", "Speculative or paused. Excluded from Backlog, Pipeline, the Forecast total, the revenue charts and every KPI tile."],
            ],
          },
          { note: "A \"No\" row still appears on its project, and its months still appear in the forecast line of the Invoiced vs Forecast chart, because that chart deliberately plots the raw fee plan. That is the one place the two differ." },
        ],
      },
      {
        id: "scenario",
        title: "Scenario — Confirmed vs Forecast",
        blocks: [
          {
            defs: [
              ["Confirmed", "Only signed work (contract Yes), at full value."],
              ["Forecast", "Signed plus tentative work, at full value — the ceiling if everything currently proposed is won."],
            ],
          },
          { warn: "The scenario toggle moves *money only*. Resourcing, the capacity matrix and the utilisation tiles always count every live project, because people are booked whether or not the contract is signed." },
        ],
      },
      {
        id: "backlog-pipeline",
        title: "Backlog, Pipeline and the Forecast identity",
        blocks: [
          {
            defs: [
              ["Backlog", "Fees on signed work (contract Yes) in the visible window."],
              ["Pipeline", "Fees on tentative work in the visible window."],
            ],
          },
          { p: "These must always hold, for the same window:" },
          {
            list: [
              "In *Confirmed* scenario — Confirmed total = Backlog.",
              "In *Forecast* scenario — Forecast total = Backlog + Pipeline.",
            ],
          },
          { p: "If those ever fail to add up, that is a bug worth reporting rather than a rounding quirk." },
        ],
      },
      {
        id: "raw-weighted",
        title: "Raw vs weighted (probability)",
        blocks: [
          { p: "Each scope row carries a *probability* — the likelihood, 0–100%, that its fee is actually invoiced." },
          {
            defs: [
              ["Raw forecast", "Fees added up as entered. No probability applied. This is what the firm-wide charts and totals use."],
              ["Weighted forecast", "Each row's fees multiplied by its probability first. A more cautious read, shown on your Summary next to the raw figure."],
            ],
          },
        ],
      },
      {
        id: "gross-net",
        title: "Gross fee, net fee and sub-consultants",
        blocks: [
          { p: "Sub-consultant costs live in their own section under the fee grid. You type them as *positive* figures; they are stored as a deduction." },
          {
            defs: [
              ["Gross forecast", "The full fee the client is billed, sub-consultant costs included."],
              ["Net fee", "Gross minus sub-consultant costs — what the firm actually keeps. This is the figure profitability is measured against."],
            ],
          },
          { note: "Invoicing always works from the *gross* total, because that is what the client pays." },
        ],
      },
      {
        id: "forecast-vs-invoiced",
        title: "Forecast vs invoiced",
        blocks: [
          {
            defs: [
              ["Forecast", "The fee plan: what you expect to bill each month, entered on the fee grid."],
              ["Invoiced", "The record of what was actually billed, entered on the invoicing grid."],
              ["Draft month", "Any invoiced month with figures typed but not yet signed off. Still editable."],
              ["Confirmed month", "Signed off by clicking the month's status button. The cells lock; click again to revert to draft. Confirming touches invoicing only — it never copies to or locks the fee plan."],
              ["Variance pill", "A small red flag on an invoiced cell when it differs from that row's forecast for the same month, e.g. \"−$4,000 vs forecast\". It clears when the two match."],
            ],
          },
          { warn: "Months later than the as-at month cannot be invoiced — they are locked amber with a padlock. Invoicing records what has *already* been billed. If figures were entered forward before this rule existed they stay visible, flagged and read-only." },
        ],
      },
      {
        id: "utilisation",
        title: "Utilisation, FTE and leave",
        blocks: [
          {
            defs: [
              ["FTE", "Full-time equivalent. 1.00 is one full person; 40 hours a week is 1 FTE."],
              ["Utilisation %", "Share of full-time. 40% is 16 hours a week."],
              ["Person-month", "One person, one month. \"20 over-allocated person-months\" could be 5 people overloaded for 4 months, or 2 people for 10."],
              ["Leave", "Counted *on top of* booked work, never by shrinking the denominator. A month booked to 80% with a full week away reads 105%."],
            ],
          },
          { p: "That additive rule is used everywhere: the matrix cells and their colours, the Avg column, the Avg utilisation tile, the Over-allocated person-months tile, all three Capacity insight cards, and the Sustained over-allocation alert." },
          {
            table: {
              head: ["Colour", "Meaning"],
              rows: [
                ["Blank", "Nothing booked and no leave"],
                ["Available", "Under 80% — room for more work"],
                ["Balanced", "80–100% — comfortably full"],
                ["Over", "Above 100% — more booked than the person has"],
              ],
            },
          },
          { p: "*Sustained over-allocation* is the amber banner on the Overview. It flags anyone over 100% for *three or more consecutive months* — a persistent run worth acting on, as opposed to a single peak month." },
        ],
      },
      {
        id: "fee-shapes",
        title: "Fee types, disbursements and subsets",
        blocks: [
          {
            defs: [
              ["Fixed", "A fixed fee for that scope stage."],
              ["Time charge", "Charged on time. Pick it as the row's fee type — it is not a separate kind of row."],
              ["Disbursements", "Just another stage in the stage dropdown. Add a scope row and choose it."],
              ["Subset", "A child row nested under a scope stage, for splitting one stage into parts. It inherits the parent's stage name, which is what keeps stage rollups correct."],
              ["Sub-consultant row", "A pinned row for pass-through costs, kept in its own section and deducted from the gross fee."],
            ],
          },
        ],
      },
      {
        id: "project-status",
        title: "Project status and the risk flag",
        blocks: [
          {
            defs: [
              ["In Progress", "Actively being worked on."],
              ["On Hold", "Paused."],
              ["Potential", "An opportunity not yet won."],
              ["Completed", "Finished. Listed separately in the portfolio but still counted in every total, chart and history."],
              ["Risk flag", "Marks a project as needing principal attention. It surfaces on the admin Overview so it does not get lost."],
            ],
          },
        ],
      },
      {
        id: "rates",
        title: "Tiers, rates and the driver rate",
        blocks: [
          { p: "People sit in tiers — Managing Principal, Principal, Associate Principal, Associate, Architectural Designer, plus any custom tier an admin adds. Each person carries a monthly and an hourly charge-out rate on Personnel." },
          {
            defs: [
              ["Tier-average rate", "The average rate of the active people in that tier. Team rows are priced at it, so a TBD row costs what a real person of that tier costs."],
              ["Driver rate", "A flat per-person-month rate including labour, overheads and target margin. Used for the \"suggested invoice\" estimate and as a fallback before rates are filled in."],
              ["Personnel cost", "Every team row (named and TBD) multiplied by its tier-average rate — monthly rows by the monthly rate, hourly rows by hours × hourly rate."],
            ],
          },
        ],
      },
      {
        id: "currency",
        title: "Currency and exchange rates",
        blocks: [
          { p: "Fees are always entered and stored in NZD. Choosing a client-facing currency on a project adds a second figure in small text beneath the NZD ones. Firm-wide reporting stays in NZD." },
          {
            list: [
              "The rate you save is *locked* to the project.",
              "Forecast months can be shown at the live European Central Bank rate alongside the locked one.",
              "Months already gone, and every invoiced figure, stay at the locked rate — so nothing already reported can move under you.",
              "The ECB publishes once per working day, so on a Monday in New Zealand the newest available day is Friday's. The \"as at\" date is always shown.",
              "\"Use today's rate\" only fills the field. It never restates a figure you have already saved.",
            ],
          },
        ],
      },
      {
        id: "tbd",
        title: "TBD team rows",
        blocks: [
          { p: "When you know you need, say, an Associate but not which one, add a row as *Any Associate — TBD*. It is priced at the tier average and plans capacity like any other row." },
          { p: "TBD rows go through the same approval as named people — management assigns the actual person when approving." },
        ],
      },
    ],
  },

  // ──────────────────────────────────────────── 3 · ADMIN PAGE BY PAGE
  {
    id: "admin-pages",
    title: "Page by page · firm-wide screens",
    blurb: "The screens that cover the whole firm — what each one is for, and what every control does.",
    topics: [
      {
        id: "p-overview",
        title: "Overview",
        roles: ["admin"],
        blocks: [
          { p: "The firm's position on one screen. Tiles first, then the alert, then the charts." },
          {
            defs: [
              ["Total backlog", "Signed fees in the window. Same in both scenarios."],
              ["Pipeline value", "Tentative fees in the window. Same in both scenarios."],
              ["Confirmed / Forecast total", "Follows the scenario toggle, and equals Backlog, or Backlog + Pipeline, for the same window."],
              ["Live projects", "Projects not marked Completed."],
              ["Avg utilisation", "Average capacity used across every person × every month in view, leave included. Counts all live work regardless of scenario."],
              ["Over-allocated person-months", "How many person-months sit above 100%, leave included."],
            ],
          },
          { p: "*Sustained over-allocation* — the amber banner listing anyone over 100% for three or more consecutive months, with the run of months." },
          { p: "*Firm summary* (top right) downloads a standalone HTML page of the firm-wide content — the six tiles, monthly revenue with the sub-consultant band, Backlog vs Pipeline, Invoiced vs Forecast, the stage rollup and contract status — with the scenario, period and generation time stated on it. Charts are drawn as static images inside the file, so it opens offline with nothing to load. Built for circulation to all staff under the Employee Share Scheme, so it carries no project, principal or personnel detail and names nobody: the over-allocation banner, the submissions widget and the top-five table are all left out. Admin only." },
          { p: "Charts, in order: monthly revenue, Backlog vs Pipeline, Invoiced vs Forecast, the design-stage rollup, contract status, and highest-value projects." },
          { note: "The arrows under the first chart scroll *history* — they shift only the two revenue charts, one or three months at a time. The tiles stay pinned to the firm's live position so you can hold a past window against today's numbers." },
          { p: "Below the charts: *report notes* for the month's commentary, and the *submissions widget* showing which principals have updated their projects this month." },
        ],
      },
      {
        id: "p-sheet",
        title: "Sheet",
        roles: ["admin"],
        blocks: [
          { p: "The master worksheet: every project × scope row × month, with people underneath. Use it when you want the whole book in one table rather than a chart." },
          {
            list: [
              "Projects open *collapsed*. Click a project row to open it, or use *Expand all* / *Collapse all*. The counter reads \"3 of 29 open\".",
              "The *up and down arrows* on a project row set your own running order. It is saved to your login and does not change anyone else's sheet — and it is separate from the order in My Projects.",
              "*Show people rows* adds each project's team allocations under its scope rows.",
              "*CSV* exports what you are looking at.",
              "The two rows above the projects are firm totals: monthly revenue under the active scenario, and team FTE deployed that month.",
              "Each project's *SubTotal* row adds its scope rows month by month; the figure at the right is that project's full forecast value.",
            ],
          },
        ],
      },
      {
        id: "p-capacity",
        title: "Capacity matrix",
        blocks: [
          { p: "People down the side, months across the top. Each cell is one person's planned load for that month across every project they are on." },
          {
            list: [
              "*Units* — % of full-time, FTE, or hours per week. Percentages include leave; FTE and hours are absolute booked quantities.",
              "*Avg* — the average of that person's months in view, leave included, the same way each cell counts it.",
              "*Click any cell* to open the drill-down: total, booked, leave, and the project-by-project split for that month.",
              "*Include proposed* (admin) adds pending proposals into the cells as an outlined split, with the approved-only figure still visible. It is independent of the scenario toggle.",
              "*Insight cards* — most over-allocated, most spare capacity, tightest months. All three count leave.",
            ],
          },
          { note: "A person with no Personnel record — a leftover from a spreadsheet import — carries a small remove icon for admins. Anyone with a real Personnel record is marked departed on Personnel instead." },
        ],
      },
      {
        id: "p-approvals",
        title: "Approvals",
        roles: ["admin"],
        blocks: [
          { p: "The second tab on Capacity, admin only. Principals' proposed resourcing lands here, grouped by project, with a count badge on the navigation." },
          { p: "Each item shows the cost impact, the affected person's capacity *before and after* for each month, and the project's margin with the proposal included — so the decision can be made without leaving the queue." },
          {
            list: [
              "*Approve* — the allocation joins the capacity plan immediately.",
              "*Reject* — it is marked rejected and stays visible to the principal.",
              "*Edit* — adjust the months or load before approving.",
              "For a *TBD* row, assign the named person as you approve.",
            ],
          },
        ],
      },
      {
        id: "p-principals",
        title: "Principals",
        roles: ["admin"],
        blocks: [
          { p: "Two views, switched top-right." },
          {
            defs: [
              ["By Principal", "Each principal's portfolio: project count, raw and weighted totals, and the month-by-month shape of their book."],
              ["All Projects", "Every project in the firm ranked in one table, filterable to a single principal. Exportable as CSV."],
            ],
          },
        ],
      },
      {
        id: "p-allocation",
        title: "Allocation",
        roles: ["admin"],
        blocks: [
          { p: "Where the money sits, cut four ways for the active scenario: by month, by design stage, by contract status, and by principal — each with its share of the total. One CSV export covers all four." },
        ],
      },
      {
        id: "p-leave",
        title: "Leave",
        blocks: [
          { p: "Active people × 52 weeks, grouped under month headers." },
          {
            list: [
              "*Click a cell to cycle* none → half week → full week → none. It saves immediately.",
              "*Admins and the leave editor* can write. Principals see the same grid read-only.",
              "Leave *adds to* a person's load in the affected month — a full week is about +25% on top of whatever is booked — so overload warnings account for who is actually here.",
            ],
          },
        ],
      },
      {
        id: "p-personnel",
        title: "Personnel",
        roles: ["admin"],
        blocks: [
          { p: "The firm's people record and rate card — admin only, and the source of truth for tiers and charge-out rates." },
          {
            list: [
              "*Add person* for one, *bulk upload* for a spreadsheet of many.",
              "*Add tier* creates a custom tier beyond the system five; it then appears in every tier picker.",
              "The strip at the top shows the *firm-average* monthly and hourly charge-out across active people.",
              "Open a person to edit tier, status (active / on leave / departed), and rates. Changing a rate appends a row to their *rate history* rather than overwriting it.",
              "*Login* grants that person a portfolio account; the password is shown once.",
              "Departed people move into their own collapsed section and stay in history.",
            ],
          },
        ],
      },
      {
        id: "p-users",
        title: "Users",
        roles: ["admin"],
        blocks: [
          { p: "Logins and what they can reach." },
          {
            list: [
              "*Add user* — name, email, role, and the link that scopes them.",
              "*Linked principal* gives a principal or leave-editor login its portfolio.",
              "*Linked personnel record* is what an associate login is tied to.",
              "The generated password appears *once* — copy it before closing.",
              "You cannot change your own role, and the last admin cannot be demoted.",
            ],
          },
        ],
      },
    ],
  },

  // ──────────────────────────────────────── 4 · PORTFOLIO PAGE BY PAGE
  {
    id: "portfolio-pages",
    title: "Page by page · your portfolio",
    blurb: "The principal, associate and leave-editor screens.",
    roles: ["principal", "associate", "leave_editor", "admin"],
    topics: [
      {
        id: "p-my-portfolio",
        title: "My Portfolio",
        blocks: [
          { p: "Your projects, with each one's forecast value and when it was last edited. Completed projects sit behind a toggle so the working list stays short." },
          { p: "*Confirm invoicing · [month]* tells management your invoicing for the month is done and can be relied on. Once pressed, a confirmed badge replaces the button." },
          { p: "*Add Project* opens an empty project form." },
        ],
      },
      {
        id: "p-my-summary",
        title: "My Summary",
        blocks: [
          { p: "Your portfolio in totals: raw forecast, weighted forecast, project count, and invoiced for the visible window — then a month-by-month chart and table, and your project list." },
          { p: "*Print* produces a clean A4 version with the navigation stripped, for a PDF or a paper handout. Charts are dropped from print because they do not reproduce reliably; the table underneath carries the same numbers." },
        ],
      },
      {
        id: "p-capacity-principal",
        title: "Capacity (principal view)",
        roles: ["principal", "associate"],
        blocks: [
          { p: "The same firm-wide matrix admins see, so you can find who has room before you propose resourcing." },
          { note: "It carries *no money figures*. You see everyone's load and leave, and nobody else's fees." },
        ],
      },
      {
        id: "p-leave-readonly",
        title: "Leave (read-only)",
        roles: ["principal"],
        blocks: [
          { p: "You can see the whole firm's leave calendar but not change it. Ask an admin or the leave editor to book or amend leave — the server enforces this, not just the screen." },
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────── 5 · PROJECT FORM
  {
    id: "project-form",
    title: "The project form, section by section",
    blurb: "Where all the numbers actually come from.",
    topics: [
      {
        id: "pf-identity",
        title: "Identity",
        blocks: [
          { p: "Project number and name, client, linked principal, status, dates, display currency and the risk flag." },
          {
            list: [
              "Admins can file a project under any principal. Principals cannot re-assign — their projects are always their own.",
              "An associate must pick which principal's portfolio a new project joins; after that only an admin or the owning principal can move it.",
              "Set a display currency and the FX rate field appears. The rate locks on save.",
            ],
          },
        ],
      },
      {
        id: "pf-fees",
        title: "Fee forecast grid",
        blocks: [
          { p: "One row per scope stage. Frozen on the left: remove, stage, fee type, contract status, probability. Then contract value, notes, and a cell for every month." },
          {
            list: [
              "*Contract value* is that row's total fee. The share of the project it represents is shown beneath it.",
              "*Month cells* take a ratio (\"5/10\", \"0.5\") or a dollar figure.",
              "The *row total* carries a marker: green when the months add up to the contract value, amber when they fall short, red when they exceed it.",
              "*+ Add* a scope row, *+ Sub* for a sub-consultant row, *+ Subset* to nest a child row under a stage.",
              "The up and down arrows reorder rows; the ✕ removes one, with an undo.",
              "Past months are shaded and locked unless your account holds the past-month override.",
            ],
          },
          { note: "Changing a parent row's stage carries its subsets with it, which is what keeps the stage rollup on the Overview correct." },
        ],
      },
      {
        id: "pf-subs",
        title: "Sub-consultants",
        blocks: [
          { p: "Their own section under the fee grid. Type costs as *positive* numbers — they are stored as a deduction, and the total beneath shows what comes off the gross fee." },
          { p: "Sub-consultant fees still count in every project total, weighted by probability like any other row. They are excluded from the invoicing grid because the client is billed the gross amount." },
        ],
      },
      {
        id: "pf-invoicing",
        title: "Invoicing · actuals vs forecast",
        blocks: [
          { p: "Mirrors the fee grid's rows in the same order, so a stage lines up with itself. Enter what was actually invoiced, per stage, per month." },
          {
            list: [
              "The *Status* row under the month headers is the sign-off. Click a month to confirm every row's figure for it; the cells lock. Click again to revert to draft.",
              "Confirming is invoicing-side only. It never copies to or locks the fee plan — the two grids stay independent.",
              "*Contract value* is shared with the fee row: editing it here changes it there too.",
              "*Notes* here are invoicing-only and separate from the fee row's note.",
              "A red pill flags any cell that differs from that row's forecast for the month.",
            ],
          },
          { warn: "Months after the as-at month are locked amber with a padlock: no entry, no confirmation. Invoicing is a record of what has already been billed." },
        ],
      },
      {
        id: "pf-team",
        title: "Project team / resourcing",
        blocks: [
          { p: "One grid for named people and TBD-by-tier requests. *+ Add* asks for a tier first, then offers that tier's people plus \"Any [tier] — TBD\"." },
          {
            list: [
              "Type a month cell as a percentage (\"40%\"), a decimal (\"0.4\") or hours per week (\"16h\").",
              "Each row is priced monthly or hourly at its tier-average rate.",
              "A plane icon marks someone with leave booked in that stretch, with the dates.",
              "Admins' edits apply to the capacity plan immediately.",
              "Everyone else's edits are *proposals*: they go to management, and an amber banner keeps them visible until decided. TBD rows follow the same route — management names the person on approval.",
            ],
          },
        ],
      },
      {
        id: "pf-profitability",
        title: "Profitability and the suggested invoice",
        blocks: [
          { p: "Net fee against personnel cost, with the margin and revenue per person-month." },
          {
            defs: [
              ["Net fee", "Scope contract values (or the scheduled monthly fees where no contract value is set) minus sub-consultant costs."],
              ["Personnel cost", "Every team row, named and TBD, at its tier-average rate."],
              ["Margin colour", "Green at or above target; red once cost exceeds fee."],
              ["Suggested invoice", "People-months × the driver rate, which includes labour, overheads and target margin. Reference only — it never fills the fee in for you. If the fee sits below it, a warning says so."],
            ],
          },
        ],
      },
      {
        id: "pf-editors",
        title: "Editors, notes and saving",
        blocks: [
          { p: "*Editors* lets an admin or the owning principal invite people from Personnel to edit this project with an associate login. Admins and the owning principal can always edit." },
          { p: "*Notes / risks* is free text for anything the numbers do not carry." },
          { warn: "Nothing is written until *Save*. An unsaved marker appears as soon as you change something, and leaving the page raises a browser warning." },
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────── 6 · RECIPES
  {
    id: "how-to",
    title: "How do I…",
    blurb: "Step-by-step for the things people ask about most.",
    topics: [
      {
        id: "ht-add-project",
        title: "Add a project",
        blocks: [
          {
            steps: [
              "Open *Add Project* (portfolio) or *My Projects → Add* (admin).",
              "Fill in the project number, name and client. Pick a status — Potential if it is not won yet.",
              "Add your first scope row in the fee grid and set its contract status and probability.",
              "Enter the contract value, then spread it across months with ratios or figures.",
              "Add the team you need under Project team / resourcing.",
              "Press *Save*.",
            ],
          },
        ],
      },
      {
        id: "ht-spread-fee",
        title: "Spread a fee across months without a calculator",
        blocks: [
          {
            steps: [
              "Set the row's *contract value* first — the ratios work off it.",
              "In the first month type \"1/4\" for a quarter of the fee.",
              "Repeat across the months you want; type \"0.5\" for a half, or a plain dollar figure where a month is irregular.",
              "Check the row total marker: green means the months add up to the contract value.",
            ],
          },
        ],
      },
      {
        id: "ht-invoice",
        title: "Record what you invoiced, and sign the month off",
        blocks: [
          {
            steps: [
              "Open the project and scroll to *Invoicing · actuals vs forecast*.",
              "Find the month — it must be the as-at month or earlier — and type the amount on each stage row you billed.",
              "Check the red variance pills: they show where you billed something other than the plan. That is information, not an error.",
              "When the month is complete, click its *Status* button to confirm. The cells lock.",
              "Press *Save*.",
              "Optionally, on My Portfolio, press *Confirm invoicing · [month]* to tell management your whole portfolio is done.",
            ],
          },
        ],
      },
      {
        id: "ht-book-someone",
        title: "Book someone onto a project",
        blocks: [
          {
            steps: [
              "Open the project and go to *Project team / resourcing*.",
              "Press *+ Add*, choose the tier, then the person — or \"Any [tier] — TBD\" if you do not know yet.",
              "Type their load in each month: \"40%\", \"0.4\" or \"16h\".",
              "Watch for the plane icon — it means they have leave booked in that stretch.",
              "Press *Save*. If you are not an admin this goes to management as a proposal.",
            ],
          },
          { note: "To check who has room first, open Capacity and look for cells under 80%." },
        ],
      },
      {
        id: "ht-approve",
        title: "Approve or adjust proposed resourcing",
        roles: ["admin"],
        blocks: [
          {
            steps: [
              "Go to *Capacity → Approvals* — the badge on the navigation shows how many are waiting.",
              "Read the before/after capacity for each affected month and the margin with the proposal included.",
              "For a TBD row, pick the named person.",
              "Approve, reject, or edit the months and load first.",
            ],
          },
        ],
      },
      {
        id: "ht-leave",
        title: "Book leave",
        blocks: [
          {
            steps: [
              "Open *Leave*.",
              "Find the person's row and the week starting nearest their first day away.",
              "Click once for a half week, twice for a full week, a third time to clear it. It saves as you click.",
              "Check Capacity — their month will have risen, because leave counts on top of booked work.",
            ],
          },
          { note: "Principals see this grid read-only. Ask an admin or the leave editor." },
        ],
      },
      {
        id: "ht-reorder",
        title: "Put the Sheet in your own order",
        roles: ["admin"],
        blocks: [
          {
            steps: [
              "Open *Sheet*.",
              "Use the up and down arrows at the left of a project row.",
              "That is it — the order is saved to your login and follows you between devices. Nobody else's sheet moves.",
            ],
          },
        ],
      },
      {
        id: "ht-currency",
        title: "Show a project in another currency",
        blocks: [
          {
            steps: [
              "Open the project and set *Currency* in the Identity section.",
              "Either type the rate you agreed, or press *Use today's rate* to fill in the latest ECB rate — the \"as at\" date is shown next to it.",
              "Press *Save*. The rate locks, and NZD figures gain a second line in the chosen currency.",
            ],
          },
          { note: "Nothing you have already reported can move: past months and every invoiced figure stay at the locked rate." },
        ],
      },
      {
        id: "ht-export",
        title: "Get numbers out — CSV or PDF",
        blocks: [
          {
            list: [
              "*Sheet → CSV* for the whole worksheet as you have it arranged.",
              "*Allocation → CSV* for the four money cuts in one file.",
              "*Principals → All Projects → CSV* for the firm-wide project ranking.",
              "*My Summary → Print* for an A4 PDF of your portfolio.",
            ],
          },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────── 7 · TROUBLESHOOTING
  {
    id: "trouble",
    title: "Why can't I… / why does it say…",
    blurb: "The things that look broken but are the app doing its job.",
    topics: [
      {
        id: "tb-locked-cell",
        title: "This cell won't let me type",
        blocks: [
          {
            list: [
              "*Shaded past month* — history is locked. Editing past months needs a per-account override granted to a named few by firm management.",
              "*Amber with a padlock, on invoicing* — the month is later than the as-at month, so it cannot be invoiced.",
              "*Green with a tick, on invoicing* — the month is confirmed. Click its status button to revert to draft first.",
              "*Read-only leave grid* — you are a principal; leave is written by admins and the leave editor.",
            ],
          },
        ],
      },
      {
        id: "tb-approval",
        title: "My resourcing change didn't take effect",
        blocks: [
          { p: "If you are not an admin, resourcing is a *proposal*. It sits with management until approved, and the amber banner on the project says so. It does not count on the capacity plan until then." },
          { p: "Admins' resourcing edits apply immediately on save." },
        ],
      },
      {
        id: "tb-red-cell",
        title: "Someone is red on Capacity but their projects don't add up to that",
        blocks: [
          { p: "Leave is almost certainly the difference. It counts on top of booked work, so 80% booked plus a full week away reads 105%." },
          { p: "Click the cell — the drill-down separates total, booked and leave, and lists the projects." },
        ],
      },
      {
        id: "tb-invoiced-zero",
        title: "The Invoiced vs Forecast chart shows no invoiced bars",
        blocks: [
          { p: "The chart plots the months in view. If the actuals recorded so far are all *earlier* than the window, there is nothing to draw — that is the chart being accurate, not empty." },
          { p: "Use the arrows under the revenue chart to scroll back to the months that hold the actuals, or widen the Period." },
        ],
      },
      {
        id: "tb-no-money",
        title: "I can see everyone's capacity but no fees",
        roles: ["principal", "associate"],
        blocks: [
          { p: "That is deliberate. Principals get the firm-wide capacity matrix so they can find who is free, with every other principal's revenue stripped out." },
        ],
      },
      {
        id: "tb-mismatch",
        title: "Two screens are showing different totals",
        blocks: [
          { p: "Nine times out of ten it is one of these four:" },
          {
            list: [
              "*Scenario* — Confirmed excludes tentative work; Forecast includes it.",
              "*Period* — one screen is narrowed to fewer months. Check \"Showing X of Y months\".",
              "*Raw vs weighted* — your Summary shows both; the firm-wide charts use raw.",
              "*Gross vs net* — net takes sub-consultant costs off; invoicing works from gross.",
            ],
          },
          { p: "If none of those explains it, say so — Backlog + Pipeline must equal the Forecast total for the same window, and a real mismatch is a bug." },
        ],
      },
      {
        id: "tb-orphan",
        title: "Someone is on the capacity matrix who doesn't work here",
        blocks: [
          { p: "Rows with no Personnel record are leftovers from a spreadsheet import. They carry a small remove icon for admins, which converts their bookings to TBD rows of a tier you choose." },
          { p: "Anyone who *does* have a Personnel record should be marked *departed* on Personnel instead, so their history survives." },
        ],
      },
      {
        id: "tb-lost-edits",
        title: "I lost my edits",
        blocks: [
          { p: "Project forms do not autosave — by design, so a half-finished thought never reaches the firm's numbers. Press *Save*. An unsaved marker shows while there are pending changes, and leaving the page warns you." },
          { p: "The leave calendar and the approvals queue are the exceptions: they save the instant you click." },
        ],
      },
      {
        id: "tb-locked-out",
        title: "I'm locked out",
        blocks: [
          { p: "Five failed attempts on one email, or from one network, within 15 minutes stops further tries. Wait the 15 minutes, or ask an admin to issue a new password on the Users page." },
        ],
      },
    ],
  },

  // ────────────────────────────────────────────────────── 8 · REFERENCE
  {
    id: "reference",
    title: "Quick reference",
    blurb: "Tables to skim when you just need the answer.",
    topics: [
      {
        id: "ref-roles",
        title: "Who can do what",
        blocks: [
          {
            table: {
              head: ["", "Admin", "Principal", "Associate", "Leave editor"],
              rows: [
                ["See firm-wide money", "Yes", "Own portfolio", "Invited projects", "Firm-wide"],
                ["Edit projects", "Any", "Own", "Invited", "Firm-wide"],
                ["Resourcing takes effect", "Immediately", "After approval", "After approval", "After approval"],
                ["Approve resourcing", "Yes", "No", "No", "No"],
                ["Capacity matrix", "Yes, with money", "Yes, no money", "Yes, no money", "No"],
                ["Edit leave", "Yes", "Read-only", "No", "Yes"],
                ["Personnel & rates", "Yes", "No", "No", "No"],
                ["Users & logins", "Yes", "No", "No", "No"],
                ["Re-feed data, backups", "Yes", "No", "No", "No"],
              ],
            },
          },
        ],
      },
      {
        id: "ref-entry",
        title: "What you can type in a cell",
        blocks: [
          {
            table: {
              head: ["Where", "Accepted", "Means"],
              rows: [
                ["Fee / invoiced month", "35000", "$35,000"],
                ["Fee / invoiced month", "5/10 or 0.5", "That share of the row's contract value"],
                ["Team month", "40%", "40% of full time"],
                ["Team month", "0.4", "The same, as a decimal"],
                ["Team month", "16h", "16 hours a week of a 40-hour week"],
                ["Leave week", "click", "none → half week → full week → none"],
              ],
            },
          },
        ],
      },
      {
        id: "ref-colours",
        title: "Colour and marker key",
        blocks: [
          {
            table: {
              head: ["You see", "It means"],
              rows: [
                ["Small dot on a month header", "The as-at month"],
                ["Shaded month column", "In the past — read-only for most accounts"],
                ["Amber cell with a padlock", "Later than the as-at month — invoicing not allowed"],
                ["Green month with a tick", "Invoicing confirmed for that month"],
                ["Red pill on an invoiced cell", "Differs from that row's forecast"],
                ["Red capacity cell", "Over 100%, leave included"],
                ["Amber banner on a project", "Resourcing awaiting approval"],
                ["Amber bar across the top", "You are acting as a principal (View as)"],
                ["Badge on the Capacity tab", "Approvals waiting"],
              ],
            },
          },
        ],
      },
      {
        id: "ref-sources",
        title: "Where each headline number comes from",
        blocks: [
          {
            table: {
              head: ["Number", "Built from"],
              rows: [
                ["Backlog", "Scope rows with contract Yes, months in view"],
                ["Pipeline", "Scope rows with contract Tentative, months in view"],
                ["Forecast total", "Backlog + Pipeline (Confirmed scenario shows Backlog alone)"],
                ["Weighted forecast", "Each scope row's months × its probability"],
                ["Net fee", "Scope contract values minus sub-consultant costs"],
                ["Invoiced", "The invoicing grid, per stage, per month"],
                ["Utilisation", "Team rows on all live projects, plus leave, over each person's full-time capacity"],
                ["Personnel cost", "Team rows × tier-average rate"],
                ["Suggested invoice", "People-months × driver rate"],
              ],
            },
          },
        ],
      },
      {
        id: "ref-help",
        title: "Still stuck?",
        blocks: [
          { p: "Two things make a question answerable quickly: the *page* you were on, and the *project number* or *person* involved. A screenshot beats a description." },
          { p: "Tooltips are everywhere — the small circled i next to a heading or a tile explains that specific figure, and always matches this manual." },
        ],
      },
    ],
  },
];

// Flattened searchable text per topic — built once, used by the search box.
const textOf = (blocks) =>
  blocks
    .map((b) => {
      if (b.p) return b.p;
      if (b.note) return b.note;
      if (b.warn) return b.warn;
      if (b.list) return b.list.join(" ");
      if (b.steps) return b.steps.join(" ");
      if (b.defs) return b.defs.map(([t, d]) => `${t} ${d}`).join(" ");
      if (b.table) return [...b.table.head, ...b.table.rows.flat()].join(" ");
      return "";
    })
    .join(" ");

export const MANUAL_INDEX = MANUAL.flatMap((section) =>
  section.topics.map((topic) => ({
    sectionId: section.id,
    topicId: topic.id,
    haystack: `${section.title} ${topic.title} ${textOf(topic.blocks)}`
      .toLowerCase()
      .replace(/\*/g, ""),
  }))
);
